import React, { useState, useEffect, useRef, memo } from 'react';
import './TypingSpeechBubble.css';

// Use a module-level ref to track if typing has ever started in this session
// This prevents restart on remounts, but resets on page refresh (which is desired)
let globalTypingStarted = false;

const TypingSpeechBubble = memo(({ message, userName, onComplete, isFadingOut: isFadingOutProp = false }) => {
  // If typing already started globally, initialize as invisible to prevent flicker on remount
  const shouldStartVisible = !globalTypingStarted;
  
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [isVisible, setIsVisible] = useState(shouldStartVisible);
  const [isFadingOut, setIsFadingOut] = useState(false);
  
  // Track if typing has started to prevent restart on prop changes or remounts
  const hasStartedRef = useRef(false);
  const fullMessageRef = useRef('');
  const onCompleteRef = useRef(onComplete);
  const hideTimeoutRef = useRef(null);
  const bubbleContainerRef = useRef(null);
  const fadeOutPositionRef = useRef({ top: 0, left: 0 });

  // Initialize on mount - capture initial message and prevent restart
  useEffect(() => {
    // If typing has already started globally, don't show component
    if (globalTypingStarted && !hasStartedRef.current) {
      setIsVisible(false);
      // Call onComplete immediately to prevent flicker
      if (onComplete) {
        // Use setTimeout to avoid state updates during render
        setTimeout(() => {
          onComplete();
        }, 0);
      }
      return;
    }
    
    // Initialize message on first render only
    if (!hasStartedRef.current) {
      const initialMessage = message.replace('{user}', userName || 'there');
      fullMessageRef.current = initialMessage;
      hasStartedRef.current = true;
      globalTypingStarted = true;
      setIsVisible(true); // Ensure it's visible when starting fresh
    }
  }, []); // Only run once on mount

  // Update callback ref when it changes
  useEffect(() => {
    if (onCompleteRef.current !== onComplete) {
      onCompleteRef.current = onComplete;
    }
  }, [onComplete]);

  // Handle external fade-out trigger (from parent)
  // If parent passes isFadingOut={true}, capture position and start fade-out
  useEffect(() => {
    if (isFadingOutProp && !isFadingOut && isVisible && bubbleContainerRef.current) {
      // Capture bubble position before fade-out starts
      const rect = bubbleContainerRef.current.getBoundingClientRect();
      fadeOutPositionRef.current = {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      };
      setIsFadingOut(true);
    }
  }, [isFadingOutProp, isFadingOut, isVisible]);

  // Typing animation effect
  useEffect(() => {
    // Skip if not visible or not started
    if (!isVisible || !hasStartedRef.current) {
      return;
    }
    
    const fullMessage = fullMessageRef.current;
    
    if (currentIndex < fullMessage.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(fullMessage.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, 50); // Typing speed - 50ms per character

      return () => clearTimeout(timeout);
    } else {
      setIsTyping(false);
      
      // After typing completes, wait 2 seconds then start fade out
      const fadeOutTimeout = setTimeout(() => {
        // Capture bubble position before fade-out starts to prevent movement
        if (bubbleContainerRef.current) {
          const rect = bubbleContainerRef.current.getBoundingClientRect();
          fadeOutPositionRef.current = {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width
          };
        }
        setIsFadingOut(true);
        
        // Wait for fade-out animation to complete (0.5s) before hiding
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
        }, 500); // Match the fadeOut animation duration
      }, 2000); // 2 seconds after typing completes

      return () => {
        clearTimeout(fadeOutTimeout);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      };
    }
  }, [currentIndex, isVisible]); // Only depend on currentIndex and isVisible

  if (!isVisible) return null;

  // Use the prop if provided (from parent), otherwise use internal state
  const fadingOut = isFadingOutProp !== undefined ? isFadingOutProp : isFadingOut;
  
  // Apply inline styles for fixed position during fade-out if position was captured
  const fixedStyle = fadingOut && fadeOutPositionRef.current.width > 0 ? {
    position: 'fixed',
    top: `${fadeOutPositionRef.current.top}px`,
    left: `${fadeOutPositionRef.current.left}px`,
    width: `${fadeOutPositionRef.current.width}px`,
    transform: 'translateX(0)',
    marginTop: 0
  } : {};

  return (
    <div 
      ref={bubbleContainerRef}
      className={`speech-bubble-container ${fadingOut ? 'fade-out fixed-position' : ''}`}
      style={fixedStyle}
    >
      <div className="speech-bubble">
        <div className="speech-bubble-content">
          <span>{displayedText}</span>
        </div>
      </div>
      <div className="speech-bubble-tail"></div>
    </div>
  );
});

TypingSpeechBubble.displayName = 'TypingSpeechBubble';

export default TypingSpeechBubble;

